import { BaseClient } from './baseClient';

/**
 * Credits are the one generic usage currency. The ledger is append-only, so a
 * transaction row is read-only history: `amount` is unsigned and `type` carries
 * the direction (DEBIT takes credits away, GRANT/REFUND give them back).
 */
export type CreditTransactionType = 'GRANT' | 'DEBIT' | 'REFUND' | 'ADJUSTMENT';

export interface CreditTransaction {
    id: string;
    type: CreditTransactionType;
    source: string;
    amount: number;
    balanceAfter: number;
    /** Metered action the charge paid for, e.g. `SUPPLIER_RESEARCH`. */
    useCase?: string;
    quantity?: number;
    createdAt: string;
}

/**
 * `GET /orgs/:orgId/credits` — balance plus the newest slice of the ledger.
 * An org with no account yet reads as balance 0 with an empty ledger; that is
 * a real answer, not an error.
 */
export interface OrgCredits {
    orgId: string;
    balance: number;
    lifetimeGranted: number;
    lifetimeSpent: number;
    transactions: CreditTransaction[];
    pagination: { page: number; limit: number; total: number };
}

/**
 * `GET /orgs/:orgId/credits/estimate` — what a quantity would cost, priced
 * against the same meter version the charge uses. No debit. `rate` is the
 * price of one unit, which is what a screen states BEFORE it knows the
 * quantity ("100 credits per component"). Rates are admin-editable, so a
 * screen must read this rather than hardcode a number.
 */
export interface CreditEstimate {
    credits: number;
    unit: string;
    rate: number;
}

export type CreditRequestStatus = 'PENDING' | 'GRANTED' | 'DECLINED';

/**
 * What the buyer was doing when they ran short. Both parts are optional, and
 * no amount is ever sent: how much to grant is the platform admin's call.
 * `shortfall` carries all three figures or none.
 */
export interface CreditRequestContext {
    /** The RFQ's display id (`RFQ-003`), never the stored one with the org code. */
    rfqId?: string;
    shortfall?: { components: number; cost: number; balance: number };
}

/** A member-facing credit request: names, never a colleague's address. */
export interface CreditRequest {
    id: string;
    orgId: string;
    status: CreditRequestStatus;
    context: CreditRequestContext;
    requestedBy: { id: string; name: string };
    createdAt: string;
    /** DECLINED only: when the decision was made. */
    decidedAt?: string;
    /** DECLINED only: when the org may ask again (decision + the cooldown). May already be past. */
    availableAt?: string;
}

class CreditsClient extends BaseClient {
    /**
     * Caller must be a member of `orgId` — UMS answers 404 ORG_NOT_FOUND for
     * anything else (deliberately indistinguishable from a nonexistent org).
     */
    async getOrgCredits(orgId: string, limit: number): Promise<OrgCredits> {
        const response = await this.api.get<OrgCredits>(`/orgs/${encodeURIComponent(orgId)}/credits`, {
            params: { limit },
        });
        return response.data;
    }

    /** 403 FEATURE_DISABLED when the meter is off — the caller shows nothing. */
    async getEstimate(orgId: string, useCase: string, quantity: number): Promise<CreditEstimate> {
        const response = await this.api.get<CreditEstimate>(
            `/orgs/${encodeURIComponent(orgId)}/credits/estimate`,
            { params: { useCase, quantity } }
        );
        return response.data;
    }

    /**
     * 201 with the new request. 409 CREDIT_REQUEST_PENDING when the org already
     * has one open (one at a time), 409 CREDIT_REQUEST_RECENTLY_DECLINED (with
     * `error.availableAt`) while the wait after a decline runs.
     */
    async requestCredits(orgId: string, context: CreditRequestContext): Promise<CreditRequest> {
        const response = await this.api.post<{ request: CreditRequest }>(
            `/orgs/${encodeURIComponent(orgId)}/credit-requests`,
            context
        );
        return response.data.request;
    }

    /**
     * The org's newest PENDING or DECLINED request, or null. Newest first, so
     * a PENDING one means "waiting for our team" and a DECLINED one
     * means the last answer was no (its `availableAt` says when to ask again);
     * a granted request is neither, and reads as null.
     */
    async getCurrentCreditRequest(orgId: string): Promise<CreditRequest | null> {
        const response = await this.api.get<{ items: CreditRequest[] }>(
            `/orgs/${encodeURIComponent(orgId)}/credit-requests`,
            { params: { status: 'PENDING,DECLINED', limit: 1 } }
        );
        return response.data.items[0] ?? null;
    }
}

export const creditsClient = new CreditsClient();
